// Orgs — the tenant list, and the only place an org is created.
//
// Each row carries the two facts that decide whether an org needs attention
// (is it active, has it done anything lately) so the list itself is triage;
// the detail screen is for when the answer is "look closer".

import React from 'react';
import { api, errorText } from '../api';
import { useAsync, useDebounced } from '../useAsync';
import { navigate } from '../router';
import { ago, num } from '../format';
import { useToast } from '../ui/Toast';
import {
  Button, Chips, Empty, ErrorState, Field, Icon, PageHead, Pill, RefreshButton,
  Row, Search, Sheet, Skeleton,
} from '../ui/primitives';

const STATUS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
];

function CreateOrgSheet({ onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const org = await api.createOrg(name.trim());
      toast.ok(`${org.name} created.`);
      onCreated(org);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="New organisation" onClose={onClose}
           footer={(
             <>
               <Button onClick={onClose} disabled={busy}>Cancel</Button>
               <Button variant="primary" loading={busy} disabled={!name.trim()}
                       onClick={submit}>Create</Button>
             </>
           )}>
      <div className="stack">
        <Field label="Name" hint="Must be unique across Mono. Rename later from the org screen.">
          <input className="input" value={name} autoFocus
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && submit()}
                 placeholder="Acme Packaging" />
        </Field>
        {err && <div className="note danger">{errorText(err)}</div>}
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          An empty org can do nothing until it has users. You will be taken
          straight to it to provision the first one.
        </div>
      </div>
    </Sheet>
  );
}

export default function Orgs() {
  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState('all');
  const [creating, setCreating] = React.useState(false);
  const debouncedQ = useDebounced(q, 280);

  const { data, error, loading, at, reload } =
    useAsync(() => api.orgs({ q: debouncedQ, status, windowD: 7 }),
             [debouncedQ, status]);

  return (
    <>
      <PageHead title="Orgs"
                sub={data ? `${data.length} organisation${data.length === 1 ? '' : 's'} · 7-day activity` : 'Tenants'}
                action={<RefreshButton onClick={reload} loading={loading} at={at} />} />

      <div className="stack" style={{ marginBottom: 14 }}>
        <Search value={q} onChange={setQ} placeholder="Search organisations" />
        <div className="row" style={{ gap: 9 }}>
          <Chips options={STATUS} value={status} onChange={setStatus} />
          <div className="spacer" />
          <Button variant="primary" small onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} />New
          </Button>
        </div>
      </div>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={4} />}

      {data && data.length === 0 && (
        <Empty title="No organisations">
          {q ? 'Nothing matches that search.' : 'Create the first tenant to get started.'}
        </Empty>
      )}

      {data && data.length > 0 && (
        <div className="card">
          <div className="list">
            {data.map((o) => (
              <Row key={o.id} onClick={() => navigate(`/orgs/${o.id}`)}>
                <div className="col" style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 7 }}>
                    <span className="truncate" style={{ fontWeight: 500 }}>{o.name}</span>
                    {!o.is_active && <Pill tone="danger">off</Pill>}
                    {o.jobs_failed_window > 0 && (
                      <Pill tone="warn">{o.jobs_failed_window} failed</Pill>
                    )}
                  </div>
                  <div className="dim" style={{ fontSize: 11.5 }}>
                    {num(o.users.active)}/{num(o.users.total)} users ·{' '}
                    {num(o.jobs_window)} jobs · {o.last_activity_at ? ago(o.last_activity_at) : 'never active'}
                  </div>
                </div>
              </Row>
            ))}
          </div>
        </div>
      )}

      {creating && (
        <CreateOrgSheet onClose={() => setCreating(false)}
                        onCreated={(org) => { setCreating(false); navigate(`/orgs/${org.id}`); }} />
      )}
    </>
  );
}
